// Asset kinds: labels, colours and map/panel icons (inline SVG data URLs).

const GLYPHS = {
  load: '<path d="M5.5 11.5 12 6l6.5 5.5"/><path d="M7.5 10.5V18h9v-7.5"/>',
  pv: '<path d="M4.5 16 7 7.5h12.5L17 16Z"/><path d="M5.8 11.8h12.4M11.6 7.5 10.4 16M15.6 7.5 14.4 16"/>',
  wind: '<circle cx="12" cy="10" r="1.3"/><path d="M12 10V3.8M12 10l-5.4 3.1M12 10l5.4 3.1M12 11.5V20"/>',
  ev_charger: '<path d="M13 4 7 13h5l-1 7 6-9h-5Z"/>',
  heat_pump: '<path d="M10.5 5.5a1.5 1.5 0 0 1 3 0v7.8a3.2 3.2 0 1 1-3 0Z"/><path d="M12 10v5"/>',
  battery: '<rect x="5" y="8" width="12" height="8" rx="1.2"/><path d="M17 10.5h1.8v3H17M8 12h2.4M9.2 10.8v2.4M12.4 12h2.4"/>',
  sgen: '<path d="M4.5 12c1.8-5 4.2-5 6 0s4.2 5 6 0"/><path d="M18.5 12h1"/>',
  gen: '<circle cx="12" cy="12" r="5.5"/><path d="M8.8 12c.9-2 2.2-2 3.2 0s2.3 2 3.2 0"/>',
  ext_grid: '<path d="M6 6h12v12H6Z"/><path d="M6 12h12M12 6v12M6 6l12 12M18 6 6 18"/>',
  trafo: '<circle cx="9.6" cy="12" r="4.3"/><circle cx="14.4" cy="12" r="4.3"/>',
  switch: '<path d="M5 15h3.5l7-6.5M15.5 15H19"/><circle cx="8.5" cy="15" r="1"/>',
  bus: '<path d="M6 12h12"/><circle cx="12" cy="12" r="2.4"/>',
  line: '<path d="M5 17 19 7"/><circle cx="5" cy="17" r="1.6"/><circle cx="19" cy="7" r="1.6"/>',
  generic: '<circle cx="12" cy="12" r="3.5"/>',
};

export const KIND_META = {
  bus:        { label: 'Bus',            color: '#c4b5fd' },
  free_bus:   { label: 'Free bus',       color: '#ddd6fe' },
  line:       { label: 'Line',           color: '#a78bfa' },
  trafo:      { label: 'Transformer',    color: '#c084fc', shape: 'square' },
  switch:     { label: 'Switch',         color: '#e2e8f0', shape: 'square' },
  load:       { label: 'Load',           color: '#f472b6' },
  pv:         { label: 'PV',             color: '#facc15' },
  wind:       { label: 'Wind turbine',   color: '#5eead4' },
  ev_charger: { label: 'EV charger',     color: '#60a5fa' },
  heat_pump:  { label: 'Heat pump',      color: '#fb923c' },
  battery:    { label: 'Battery',        color: '#4ade80' },
  sgen:       { label: 'Static generator', color: '#a3e635' },
  gen:        { label: 'Generator',      color: '#a3e635' },
  ext_grid:   { label: 'External grid',  color: '#e879f9', shape: 'square' },
  shunt:      { label: 'Shunt',          color: '#94a3b8' },
  motor:      { label: 'Motor',          color: '#94a3b8' },
  ward:       { label: 'Ward',           color: '#94a3b8' },
  xward:      { label: 'Extended ward',  color: '#94a3b8' },
};

export const kindLabel = (kind) => KIND_META[kind]?.label || kind;
// Power/energy fields (MW, Mvar, MWh, MVA) step by half a unit; the rest keep free entry.
export const stepFor = (key) => (/_(mw|mvar|mwh|mva)$/.test(key) ? '0.5' : 'any');
export const kindColor = (kind) => KIND_META[kind]?.color || '#94a3b8';

export function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function svgUrl(inner) {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24">${inner}</svg>`);
}

const cache = {};

// Coloured badge with a dark glyph. `variant` = 'open' draws an open switch in red.
export function iconUrl(kind, variant) {
  const key = `${kind}:${variant || ''}`;
  if (cache[key]) return cache[key];
  const meta = KIND_META[kind] || {};
  const fill = variant === 'open' ? '#f87171' : (meta.color || '#94a3b8');
  const glyph = GLYPHS[kind] || GLYPHS.generic;
  const base = meta.shape === 'square'
    ? `<rect x="1.6" y="1.6" width="20.8" height="20.8" rx="5" fill="${fill}" stroke="#140a2a" stroke-width="1.3"/>`
    : `<circle cx="12" cy="12" r="10.4" fill="${fill}" stroke="#140a2a" stroke-width="1.3"/>`;
  cache[key] = svgUrl(`${base}<g fill="none" stroke="#1b1030" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${glyph}</g>`);
  return cache[key];
}

export const SELECTION_RING = svgUrl(
  '<circle cx="12" cy="12" r="10.6" fill="none" stroke="#ffffff" stroke-width="1.6"/>' +
  '<circle cx="12" cy="12" r="11.6" fill="none" stroke="#f0abfc" stroke-opacity="0.55" stroke-width="0.8"/>');

export const DRAFT_PIN = svgUrl(
  '<path d="M12 22s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12Z" fill="#a855f7" stroke="#fff" stroke-width="1.4"/>' +
  '<circle cx="12" cy="10" r="2.6" fill="#fff"/>');

// Voltage-level colours for lines and buses.
export function voltageColor(vn) {
  if (vn == null) return [167, 139, 250];
  if (vn >= 60) return [251, 191, 36];    // HV
  if (vn >= 1) return [167, 139, 250];    // MV
  return [240, 171, 252];                 // LV
}

export const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'bus', label: 'Buses' },
  { key: 'free_bus', label: 'Free buses', hint: 'Buses with nothing but cables attached' },
  { key: 'line', label: 'Lines' },
  { key: 'trafo', label: 'Transformers' },
  { key: 'switch', label: 'Switches' },
  { key: 'load', label: 'Loads' },
  { key: 'pv', label: 'PV' },
  { key: 'wind', label: 'Wind' },
  { key: 'ev_charger', label: 'EV chargers' },
  { key: 'heat_pump', label: 'Heat pumps' },
  { key: 'battery', label: 'Batteries' },
  { key: 'sgen', label: 'Generators' },
  { key: 'gen', label: 'Gens' },
  { key: 'ext_grid', label: 'External grid' },
];

// Does `obj` (a normalised view object) pass the filter?
export function matchesFilter(obj, filter) {
  if (!filter || filter === 'all') return true;
  if (filter === 'free_bus') return obj.table === 'bus' && obj.free;
  if (filter === 'bus') return obj.table === 'bus';
  return obj.kind === filter;
}
